import { useEffect, useRef, type RefObject } from "react"
import { useShortcuts } from "@/shortcuts/use-shortcut.ts"

/*
 * Moving through a list from the keyboard, as in Linear: j and k (or ↓ and ↑) move a highlight over
 * the list's rows, and Enter opens the highlighted one. A row is any element marked `data-row`
 * inside the container, and it opens by clicking its link: the row itself when it is one, as a
 * TicketRow is, or the link inside it marked `data-row-link`, as a DataTable row has.
 *
 * The highlight is the `data-highlighted` attribute, set on the row in the DOM rather than kept in
 * React state, so moving it re-renders nothing however long the list. The pointer and Tab move
 * where the next j or k starts from without highlighting anything, since the row under the pointer
 * or the focused link already shows itself; the keys then carry on from there, or, until then, from
 * the row marked `aria-current`, when the list has one chosen.
 *
 * The keys are the page's: they stand aside while the Member types in a field or has something
 * open over the page, and Enter passes when nothing is highlighted, so it does what it would have.
 */

/**
 * The row a move lands on: `by` 1 is down and -1 up, from `current`, the index of the row it starts
 * from, or -1 when there is none. With none, down lands on the first row and up on the last; a move
 * past either end stays there. -1 when the list is empty.
 */
export function nextRow(current: number, count: number, by: 1 | -1) {
  if (count === 0) return -1
  if (current < 0 || current >= count) return by === 1 ? 0 : count - 1
  return Math.min(Math.max(current + by, 0), count - 1)
}

/**
 * Where a key press puts the highlight, or -1 when the press passes to the page. `highlighted` is
 * the highlighted row's index and `anchor` the row pointed at or tabbed to last, each -1 when there
 * is none. A highlight already on the last row going down, or the first going up, has nowhere to go,
 * so the press scrolls the page on past the list, as it would have without one; ↓ would otherwise
 * never reach what a page holds below its list.
 */
export function landingRow(highlighted: number, anchor: number, count: number, by: 1 | -1) {
  const next = nextRow(highlighted >= 0 ? highlighted : anchor, count, by)
  return highlighted >= 0 && next === highlighted ? -1 : next
}

const highlighted = "data-highlighted"

/** The link a row opens by: the row itself, or the link it marks as its own. */
function linkOf(row: HTMLElement) {
  return row.matches("a[href]")
    ? row
    : (row.querySelector<HTMLElement>("[data-row-link]") ??
        row.querySelector<HTMLElement>("a[href]"))
}

/**
 * Moves a highlight over the `[data-row]` elements inside `container` with j/k and ↑/↓, and opens
 * the highlighted row with Enter. `enabled` turns the keys off, as while the list is loading.
 */
export function useRowNavigation(
  container: RefObject<HTMLElement | null>,
  { enabled = true }: { enabled?: boolean } = {},
) {
  // Where the next move starts when nothing is highlighted: the row last pointed at or tabbed to.
  const anchor = useRef<HTMLElement | null>(null)

  const rowsOf = () => [...(container.current?.querySelectorAll<HTMLElement>("[data-row]") ?? [])]

  const move = (by: 1 | -1) => {
    const rows = rowsOf()
    if (rows.length === 0) return false
    const current = rows.findIndex((row) => row.hasAttribute(highlighted))
    // Before the pointer or Tab has been on any row, the keys start from the row the page marks as
    // chosen (`aria-current`), as the Groups list marks the group it shows beside it.
    const pointedAt = anchor.current ? rows.indexOf(anchor.current) : -1
    const pointed =
      pointedAt >= 0 ? pointedAt : rows.findIndex((row) => row.hasAttribute("aria-current"))
    const next = rows[landingRow(current, pointed, rows.length, by)]
    if (!next) return false
    rows[current]?.removeAttribute(highlighted)
    next.setAttribute(highlighted, "")
    anchor.current = next
    next.scrollIntoView({ block: "nearest" })
    // Enter goes to a focused control before the list (a group's chevron, the sidebar link just
    // clicked, the row tabbed to before), so focus left anywhere but the highlighted row lets go of
    // it: the highlight says Enter opens that row, and then it does.
    const focused = document.activeElement
    if (focused instanceof HTMLElement && focused !== document.body && !next.contains(focused))
      focused.blur()
  }

  useShortcuts(
    {
      "row-down": () => move(1),
      "row-up": () => move(-1),
      "row-open": () => {
        const row = rowsOf().find((each) => each.hasAttribute(highlighted))
        const link = row && linkOf(row)
        if (!link) return false
        link.click()
      },
    },
    { enabled },
  )

  useEffect(() => {
    const list = container.current
    if (!list || !enabled) return
    // The pointer or focus has moved to a row: the keys carry on from it, and the keyboard's
    // highlight gives way, so two rows never look current at once.
    const follow = (event: Event) => {
      const row =
        event.target instanceof Element ? event.target.closest<HTMLElement>("[data-row]") : null
      if (!row || !list.contains(row)) return
      anchor.current = row
      for (const each of list.querySelectorAll(`[${highlighted}]`))
        if (each !== row) each.removeAttribute(highlighted)
    }
    list.addEventListener("pointermove", follow)
    list.addEventListener("focusin", follow)
    return () => {
      list.removeEventListener("pointermove", follow)
      list.removeEventListener("focusin", follow)
    }
  }, [container, enabled])
}
